import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Column, TableAction } from 'components/display/Table/types';
import { Button } from 'components/ui/Button';
import { EyeIcon } from 'components/icons/Icons';
import { CheckCircle2, XCircle } from 'lucide-react';
import ServerTable from 'shared/contexts/table-context/components/ServerTable';
import {
  FacetQueryCommand,
  mapFacetFiltersToFiltersDto
} from 'shared/contexts/table-context/facetFilters';
import {
  buildExportCommand,
  exportListCsv
} from 'shared/contexts/table-context/exportCsv';
import { OnCreateConfig } from 'shared/contexts/table-context/components/types';
import { LinkPlateformesAction } from 'back-office/shared/form/LinkPlateformesAction';
import { TunnelActionsMenu } from './TunnelActionsMenu';
import { useCreateTunnelDialog } from './create/useCreateTunnelDialog';
import { tunnelTypeLabel } from './detail/funnelEnums';
import { ReferentialKey } from 'api/referential';
import { useReferentialUtils } from 'features/referentials/hooks/useReferentialUtils';
import { funnelsAPI, productsAPI } from 'services/swagger-api';
import { TunnelAdminListItem } from 'api/funnel';
import { TableResponse } from 'slices/generic-table/genericTableSlice';

interface FunnelsAdminListProps {
  createConfig?: OnCreateConfig;
}

const fmtDate = (iso?: string | Date | null): string =>
  iso ? new Date(iso).toLocaleDateString('fr-FR') : '—';

const activeBadge = (isActive: boolean) =>
  isActive ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
      <CheckCircle2 className="h-3.5 w-3.5" />
      Actif
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-500">
      <XCircle className="h-3.5 w-3.5" />
      Inactif
    </span>
  );

export default function FunnelsAdminList({
  createConfig
}: FunnelsAdminListProps) {
  const navigate = useNavigate();
  const { getReferentialCodeFromId } = useReferentialUtils();
  // Bump pour refetch la liste après (dé)liaison de plateformes
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Création : l'écran appelant peut fournir son propre bouton ; sinon la liste propose
  // le sien, la création d'un tunnel n'ayant pas d'autre point d'entrée.
  const { canCreate, openCreateDialog } = useCreateTunnelDialog();
  const effectiveCreateConfig: OnCreateConfig | undefined =
    createConfig ??
    (canCreate
      ? {
          label: 'Créer un tunnel',
          onClick: () => openCreateDialog(() => setRefreshTrigger(t => t + 1)),
          // Seul point d'entrée de la création : il doit se voir dans un en-tête qui
          // compte déjà quatre boutons.
          color: 'orange' as const
        }
      : undefined);

  // L'œil mène à la page de gestion du tunnel : elle reprend toutes les informations
  // que l'ancien panneau latéral affichait, et permet en plus de les modifier.
  const openTunnelDetail = useCallback(
    (tunnel: TunnelAdminListItem) =>
      navigate(`/back-office/tunnels/${tunnel.id}`),
    [navigate]
  );

  const columns: Column<TunnelAdminListItem>[] = useMemo(
    () => [
      {
        key: 'code',
        label: 'Code',
        sortKey: 'identifier',
        render: v => (
          <span className="font-mono text-xs text-gray-600">{v as string}</span>
        )
      },
      {
        key: 'label',
        label: 'Désignation',
        sortKey: 'label',
        render: v => (
          <span className="font-semibold text-gray-900">{v as string}</span>
        )
      },
      {
        key: 'productLabel',
        label: 'Produit',
        sortKey: 'productLabel',
        render: (v, row) => (
          <div>
            <div className="text-sm text-gray-900">{(v as string) || '—'}</div>
            {row.productCode && (
              <div className="text-xs text-gray-500">{row.productCode}</div>
            )}
          </div>
        )
      },
      {
        key: 'branchName',
        label: 'Branche',
        sortKey: 'branch',
        render: v => (
          <span className="text-sm text-gray-700">{(v as string) || '—'}</span>
        )
      },
      {
        // TypeTunnel : 1 = Individuel, 2 = Collectif. Une valeur hors énumération (un
        // tunnel dont le type n'a jamais été renseigné) reste vide plutôt que d'être
        // annoncée « Individuel ».
        key: 'tunnelType',
        label: 'Type',
        render: v => (
          <span className="text-sm text-gray-700">
            {tunnelTypeLabel(v as number) ?? '—'}
          </span>
        )
      },
      {
        // Distributeur / représentant du tunnel (décorrélé du prélèvement)
        key: 'ownerName',
        label: 'Distributeur',
        sortKey: 'owner',
        render: v => (
          <span className="text-sm text-gray-700">{(v as string) || '—'}</span>
        )
      },
      {
        key: 'quotesCount',
        label: 'Devis',
        sortKey: 'quoteCount',
        render: v => (
          <span className="text-sm text-gray-700">{v as number}</span>
        )
      },
      {
        key: 'activeContractsCount',
        label: 'Contrats',
        sortKey: 'contractCount',
        render: v => (
          <span className="text-sm text-gray-700">{v as number}</span>
        )
      },
      {
        key: 'isActive',
        label: 'Statut',
        render: v => activeBadge(v as boolean)
      },
      {
        key: 'creationDate',
        label: 'Créé le',
        sortKey: 'date',
        render: v => (
          <span className="text-sm text-gray-700">{fmtDate(v as string)}</span>
        )
      }
    ],
    []
  );

  const fetchFunnels = useCallback(
    async (params: any): Promise<TableResponse<TunnelAdminListItem>> => {
      const result = await funnelsAPI.getAdminTunnels({
        getGenericListCommandDto: {
          pageNumber: params.pageNumber,
          pageSize: params.pageSize,
          searchString: params.searchString || undefined,
          sortBy: params.sortBy,
          sortDirection: params.sortDirection,
          filters: mapFacetFiltersToFiltersDto(params.filters)
        }
      });
      const items = result.items || [];
      return {
        items,
        totalItems: result.totalCount || items.length,
        totalPages: Math.ceil(
          (result.totalCount || items.length) / params.pageSize
        ),
        currentPage: params.pageNumber
      };
    },
    []
  );

  const fetchFacets = useCallback(
    (command: FacetQueryCommand) =>
      funnelsAPI.getAdminTunnelListFacets({
        getGenericListCommandDto: command
      }),
    []
  );

  const rowActions: TableAction<TunnelAdminListItem>[] = [
    {
      label: 'view',
      render: (row, index) => (
        <Button
          key={index}
          color="slate"
          round
          onClick={() => openTunnelDetail(row)}
        >
          <EyeIcon />
        </Button>
      )
    },
    {
      // Lier des plateformes au tunnel (présélection depuis la ligne)
      label: 'link-plateformes',
      render: (row, index) => (
        <LinkPlateformesAction
          key={index}
          title={`Plateformes du tunnel · ${row.label}`}
          resolveInitialCodes={() =>
            (row.plateformes ?? [])
              .map(o =>
                getReferentialCodeFromId(ReferentialKey.Plateforme, o.id)
              )
              .filter((c): c is string => !!c)
          }
          resolveIncludeCodes={async () => {
            // Règle métier : plateformes du tunnel ⊆ plateformes de son produit.
            if (!row.productId) return undefined;
            const res = await productsAPI.getPlateformes({
              productId: row.productId
            });
            return (res.plateformes ?? []).map(p => p.code);
          }}
          onLink={plateformeIds =>
            funnelsAPI
              .linkPlateformes({
                funnelId: row.id,
                genericLinkPlateformesCommand: { plateformeIds }
              })
              .then(() => undefined)
          }
          onSuccess={() => setRefreshTrigger(t => t + 1)}
        />
      )
    },
    {
      // Dupliquer / supprimer : proposées sur tous les tunnels, actifs ou non. La copie
      // apparaît dans la liste, inactive ; pour la suppression, c'est le back qui refuse
      // et explique lorsqu'un devis, contrat, mandat… pointe encore le tunnel.
      label: 'more',
      render: (row, index) => (
        <TunnelActionsMenu
          key={index}
          tunnel={row}
          onDuplicated={() => setRefreshTrigger(t => t + 1)}
          onDeleted={() => setRefreshTrigger(t => t + 1)}
        />
      )
    }
  ];

  // Rendu « nu » : le layout + le header (bandeau blanc) sont fournis par FunnelsView
  return (
    <ServerTable<TunnelAdminListItem>
      apiConfig={{ fetchData: fetchFunnels }}
      columns={columns}
      selectable
      serverSorting
      centerColumns
      columnCustomization={{ storageKey: 'bo-tunnels' }}
      refreshTrigger={refreshTrigger}
      facets={{ fetchFacets }}
      onExport={p =>
        exportListCsv(
          '/api/backoffice/tunnels/admin/liste/csv',
          buildExportCommand(p, mapFacetFiltersToFiltersDto(p.filters))
        )
      }
      actions={{
        ...(effectiveCreateConfig && { create: effectiveCreateConfig }),
        row: rowActions
      }}
    />
  );
}
